<!--
  SVGA 动效容器（与 lottie-animation 分工：Lottie 用 JSON，SVGA 用 .svga 文件）
  仅在客户端挂载后加载 svgaplayerweb（该库依赖 window）
-->
<template>
	<div ref="rootRef" class="svga-animation" :style="rootStyle" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
	/** .svga 资源 URL（建议 Vite `import url from '…?url'`） */
	src: {
		type: String,
		required: true,
	},
	/** 容器宽度（px 或带单位字符串） */
	width: {
		type: [Number, String],
		default: 120,
	},
	/** 为 true 时按 750 设计稿将数字 width 转为 vw（避免 inline px 撑破移动端视口） */
	useDesignViewport: {
		type: Boolean,
		default: false,
	},
	/** 设计稿宽度，与 postcss viewportWidth 一致 */
	designViewportWidth: {
		type: Number,
		default: 750,
	},
	/** false：只播放一次，并停留在最后一帧 */
	loop: {
		type: Boolean,
		default: true,
	},
	/**
	 * 非循环播放结束后是否停留在最后一帧（需 loop=false）
	 * clearsAfterStop=false + fillMode=Forward
	 */
	holdLastFrame: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits(["ready", "error"]);

const rootRef = ref(null);
let player = null;

const rootStyle = computed(() => {
	if (typeof props.width === "number") {
		if (props.useDesignViewport) {
			const vw = (props.width / props.designViewportWidth) * 100;
			return { width: `${vw}vw`, maxWidth: "100%" };
		}
		return { width: `${props.width}px` };
	}
	return { width: props.width };
});

function destroySvga() {
	if (player) {
		try {
			player.stopAnimation(true);
			player.clear();
		} catch (_) {
			/* ignore */
		}
		player = null;
	}
}

function applyPlayerPlaySettings() {
	if (!player) {
		return;
	}
	if (props.loop) {
		player.loops = 0;
		player.clearsAfterStop = true;
		player.fillMode = "Forward";
		return;
	}
	player.loops = 1;
	if (props.holdLastFrame) {
		player.clearsAfterStop = false;
		player.fillMode = "Forward";
	} else {
		player.clearsAfterStop = true;
		player.fillMode = "Backward";
	}
}

async function mountSvga() {
	if (!import.meta.client || !rootRef.value || !props.src) {
		return;
	}
	destroySvga();
	const SVGA = (await import("svgaplayerweb")).default;
	const parser = new SVGA.Parser();
	player = new SVGA.Player(rootRef.value);
	player.setContentMode("AspectFit");
	applyPlayerPlaySettings();
	parser.load(
		props.src,
		videoItem => {
			if (!player) {
				return;
			}
			player.setVideoItem(videoItem);
			player.startAnimation();
			emit("ready");
		},
		() => {
			emit("error");
		}
	);
}

onMounted(() => {
	void mountSvga();
});

onUnmounted(() => {
	destroySvga();
});

watch(
	() => props.src,
	() => {
		void mountSvga();
	}
);

watch([() => props.loop, () => props.holdLastFrame], () => {
	applyPlayerPlaySettings();
});
</script>

<style scoped>
.svga-animation {
	display: block;
	line-height: 0;
	margin: 0 auto;
}
</style>
